import { Icon } from './Icon.tsx';
import type { DhaftharuMeta, Condition, DhaftharuFieldType, BizEntity } from '../features/dhaftharu/api.ts';
import { dv } from '../i18n/dv.ts';

// ── Target encoding ───────────────────────────────────────────────────────────
// 'STATUS'
// 'RESIDENT_FIELD:<fieldId>'
// 'BUSINESS_FIELD:REGISTERED_BUSINESS:<fieldId>'
// 'BUSINESS_FIELD:HOME_BUSINESS:<fieldId>'

type TargetFieldType = 'STATUS' | DhaftharuFieldType;

interface ResolvedTarget {
  source: 'STATUS' | 'RESIDENT_FIELD' | 'BUSINESS_FIELD';
  fieldId: string | undefined;
  entity: BizEntity | undefined;
  fieldType: TargetFieldType;
  /** Options for SELECT/CHOICE/MULTISELECT. null for other types. */
  options: string[] | null;
}

const RF_PREFIX = 'RESIDENT_FIELD:';
const RB_PREFIX = 'BUSINESS_FIELD:REGISTERED_BUSINESS:';
const HB_PREFIX = 'BUSINESS_FIELD:HOME_BUSINESS:';

function resolveTarget(targetStr: string, meta: DhaftharuMeta): ResolvedTarget {
  if (targetStr === 'STATUS') {
    return { source: 'STATUS', fieldId: undefined, entity: undefined, fieldType: 'STATUS', options: null };
  }
  if (targetStr.startsWith(RF_PREFIX)) {
    const fieldId = targetStr.slice(RF_PREFIX.length);
    const field = meta.residentFields.find((f) => f.id === fieldId);
    return { source: 'RESIDENT_FIELD', fieldId, entity: undefined, fieldType: field?.type ?? 'TEXT', options: field?.options ?? null };
  }
  if (targetStr.startsWith(RB_PREFIX)) {
    const fieldId = targetStr.slice(RB_PREFIX.length);
    const field = meta.registeredBusinessFields.find((f) => f.id === fieldId);
    return { source: 'BUSINESS_FIELD', fieldId, entity: 'REGISTERED_BUSINESS', fieldType: field?.type ?? 'TEXT', options: field?.options ?? null };
  }
  if (targetStr.startsWith(HB_PREFIX)) {
    const fieldId = targetStr.slice(HB_PREFIX.length);
    const field = meta.homeBusinessFields.find((f) => f.id === fieldId);
    return { source: 'BUSINESS_FIELD', fieldId, entity: 'HOME_BUSINESS', fieldType: field?.type ?? 'TEXT', options: field?.options ?? null };
  }
  return { source: 'STATUS', fieldId: undefined, entity: undefined, fieldType: 'STATUS', options: null };
}

function conditionToTargetStr(c: Condition): string {
  if (c.source === 'STATUS') return 'STATUS';
  if (c.source === 'RESIDENT_FIELD') return `${RF_PREFIX}${c.fieldId ?? ''}`;
  return `BUSINESS_FIELD:${c.entity ?? ''}:${c.fieldId ?? ''}`;
}

// ── Operators ─────────────────────────────────────────────────────────────────

function opsForType(type: TargetFieldType): readonly string[] {
  switch (type) {
    case 'STATUS':      return ['in'];
    case 'TEXT':        return ['contains', 'equals', 'filled', 'empty'];
    case 'NUMBER':      return ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'between'];
    case 'SELECT':
    case 'CHOICE':      return ['in', 'notIn'];
    case 'MULTISELECT': return ['includesAny', 'includesAll'];
    case 'DATE':        return ['before', 'after', 'on', 'between'];
  }
}

function defaultOp(type: TargetFieldType): string {
  switch (type) {
    case 'STATUS':      return 'in';
    case 'TEXT':        return 'contains';
    case 'NUMBER':      return 'eq';
    case 'SELECT':
    case 'CHOICE':      return 'in';
    case 'MULTISELECT': return 'includesAny';
    case 'DATE':        return 'before';
  }
}

function opLabel(op: string): string {
  switch (op) {
    case 'contains':    return dv.dhaftharu.opContains;
    case 'equals':      return dv.dhaftharu.opEquals;
    case 'filled':      return dv.dhaftharu.opFilled;
    case 'empty':       return dv.dhaftharu.opEmpty;
    case 'eq':          return dv.dhaftharu.opEq;
    case 'ne':          return dv.dhaftharu.opNe;
    case 'lt':          return dv.dhaftharu.opLt;
    case 'lte':         return dv.dhaftharu.opLte;
    case 'gt':          return dv.dhaftharu.opGt;
    case 'gte':         return dv.dhaftharu.opGte;
    case 'between':     return dv.dhaftharu.opBetween;
    case 'in':          return dv.dhaftharu.opIn;
    case 'notIn':       return dv.dhaftharu.opNotIn;
    case 'includesAny': return dv.dhaftharu.opIncludesAny;
    case 'includesAll': return dv.dhaftharu.opIncludesAll;
    case 'before':      return dv.dhaftharu.opBefore;
    case 'after':       return dv.dhaftharu.opAfter;
    case 'on':          return dv.dhaftharu.opOn;
    default:            return op;
  }
}

// ── Completeness check ────────────────────────────────────────────────────────

export function isConditionComplete(c: Condition): boolean {
  // Valid target: RESIDENT_FIELD needs fieldId; BUSINESS_FIELD needs fieldId + entity
  if (c.source === 'RESIDENT_FIELD' && !c.fieldId) return false;
  if (c.source === 'BUSINESS_FIELD' && (!c.fieldId || !c.entity)) return false;

  const { op } = c;

  if (op === 'filled' || op === 'empty') return true;

  if (op === 'in' || op === 'notIn' || op === 'includesAny' || op === 'includesAll') {
    return Array.isArray(c.values) && c.values.length > 0;
  }

  if (op === 'contains' || op === 'equals') {
    return typeof c.value === 'string' && c.value.trim() !== '';
  }

  if (op === 'before' || op === 'after' || op === 'on') {
    return typeof c.value === 'string' && c.value !== '';
  }

  if (op === 'eq' || op === 'ne' || op === 'lt' || op === 'lte' || op === 'gt' || op === 'gte') {
    return typeof c.value === 'number' && !Number.isNaN(c.value);
  }

  if (op === 'between') {
    if (typeof c.value === 'number' && typeof c.value2 === 'number') {
      return !Number.isNaN(c.value) && !Number.isNaN(c.value2);
    }
    return (
      typeof c.value === 'string' && c.value !== '' &&
      typeof c.value2 === 'string' && c.value2 !== ''
    );
  }

  return false;
}

// ── Condition construction ────────────────────────────────────────────────────

/**
 * Build a well-formed Condition from resolved target + op + raw value buckets.
 * Callers pass whichever buckets are relevant; this picks the right ones for `op`.
 */
function buildCondition(
  resolved: ResolvedTarget,
  op: string,
  values: string[],
  value: string | number,
  value2: string | number,
): Condition {
  const useValues = ['in', 'notIn', 'includesAny', 'includesAll'].includes(op);
  const useValue  = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'before', 'after', 'on', 'contains', 'equals'].includes(op);
  const useBetween = op === 'between';

  if (resolved.source === 'STATUS') {
    if (useValues)  return { source: 'STATUS', op, values };
    return { source: 'STATUS', op };
  }
  if (resolved.source === 'RESIDENT_FIELD') {
    const fid = resolved.fieldId as string;
    if (useValues)   return { source: 'RESIDENT_FIELD', fieldId: fid, op, values };
    if (useValue)    return { source: 'RESIDENT_FIELD', fieldId: fid, op, value };
    if (useBetween)  return { source: 'RESIDENT_FIELD', fieldId: fid, op, value, value2 };
    return { source: 'RESIDENT_FIELD', fieldId: fid, op };
  }
  // BUSINESS_FIELD
  const fid = resolved.fieldId as string;
  const ent = resolved.entity as BizEntity;
  if (useValues)   return { source: 'BUSINESS_FIELD', fieldId: fid, entity: ent, op, values };
  if (useValue)    return { source: 'BUSINESS_FIELD', fieldId: fid, entity: ent, op, value };
  if (useBetween)  return { source: 'BUSINESS_FIELD', fieldId: fid, entity: ent, op, value, value2 };
  return { source: 'BUSINESS_FIELD', fieldId: fid, entity: ent, op };
}

function makeDefaultCondition(targetStr: string, meta: DhaftharuMeta): Condition {
  const r = resolveTarget(targetStr, meta);
  const op = defaultOp(r.fieldType);
  const defNum = 0;
  const defStr = '';
  return buildCondition(r, op, [], r.fieldType === 'NUMBER' ? defNum : defStr, r.fieldType === 'NUMBER' ? defNum : defStr);
}

function withNewOp(c: Condition, newOp: string, resolved: ResolvedTarget): Condition {
  const values = c.values ?? [];
  const value  = c.value  ?? (resolved.fieldType === 'NUMBER' ? 0 : '');
  const value2 = c.value2 ?? (resolved.fieldType === 'NUMBER' ? 0 : '');
  return buildCondition(resolved, newOp, values, value, value2);
}

// ── Value input ───────────────────────────────────────────────────────────────

function ConditionValueInput({
  condition,
  resolved,
  meta,
  onChange,
}: {
  condition: Condition;
  resolved: ResolvedTarget;
  meta: DhaftharuMeta;
  onChange: (c: Condition) => void;
}): JSX.Element | null {
  const { op } = condition;

  // Multi-select checkbox list: in / notIn / includesAny / includesAll
  if (['in', 'notIn', 'includesAny', 'includesAll'].includes(op)) {
    const rawOptions =
      resolved.source === 'STATUS'
        ? meta.statuses.map((s) => ({ value: s.value as string, label: s.label }))
        : (resolved.options ?? []).map((o) => ({ value: o, label: o }));

    const selected = condition.values ?? [];
    const toggle = (opt: string): void => {
      const next = selected.includes(opt)
        ? selected.filter((x) => x !== opt)
        : [...selected, opt];
      onChange({ ...condition, values: next });
    };

    return (
      <div className="space-y-1 pt-1">
        {rawOptions.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
        {rawOptions.length === 0 && (
          <p className="text-xs text-muted-foreground">(no options defined)</p>
        )}
      </div>
    );
  }

  // Number single input
  if (['eq', 'ne', 'lt', 'lte', 'gt', 'gte'].includes(op)) {
    const val = typeof condition.value === 'number' ? condition.value : 0;
    return (
      <input
        type="number"
        className="input w-36"
        dir="ltr"
        value={val}
        onChange={(e) =>
          onChange({ ...condition, value: e.target.value === '' ? 0 : Number(e.target.value) })
        }
      />
    );
  }

  // Between (two inputs)
  if (op === 'between') {
    if (resolved.fieldType === 'NUMBER') {
      const v  = typeof condition.value  === 'number' ? condition.value  : 0;
      const v2 = typeof condition.value2 === 'number' ? condition.value2 : 0;
      return (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            className="input w-28"
            dir="ltr"
            value={v}
            onChange={(e) =>
              onChange({ ...condition, value: e.target.value === '' ? 0 : Number(e.target.value) })
            }
          />
          <span className="text-sm text-muted-foreground">{dv.dhaftharu.andLabel}</span>
          <input
            type="number"
            className="input w-28"
            dir="ltr"
            value={v2}
            onChange={(e) =>
              onChange({ ...condition, value2: e.target.value === '' ? 0 : Number(e.target.value) })
            }
          />
        </div>
      );
    }
    // DATE between
    const v  = typeof condition.value  === 'string' ? condition.value  : '';
    const v2 = typeof condition.value2 === 'string' ? condition.value2 : '';
    return (
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          className="input w-36"
          dir="ltr"
          value={v}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        />
        <span className="text-sm text-muted-foreground">{dv.dhaftharu.andLabel}</span>
        <input
          type="date"
          className="input w-36"
          dir="ltr"
          value={v2}
          onChange={(e) => onChange({ ...condition, value2: e.target.value })}
        />
      </div>
    );
  }

  // Date single
  if (['before', 'after', 'on'].includes(op)) {
    const v = typeof condition.value === 'string' ? condition.value : '';
    return (
      <input
        type="date"
        className="input w-36"
        dir="ltr"
        value={v}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
      />
    );
  }

  // Text
  if (op === 'contains' || op === 'equals') {
    const v = typeof condition.value === 'string' ? condition.value : '';
    return (
      <input
        type="text"
        className="input"
        value={v}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
      />
    );
  }

  // filled / empty — no input
  return null;
}

// ── ConditionRow ──────────────────────────────────────────────────────────────

function ConditionRow({
  condition,
  meta,
  onChange,
  onRemove,
}: {
  condition: Condition;
  meta: DhaftharuMeta;
  onChange: (c: Condition) => void;
  onRemove: () => void;
}): JSX.Element {
  const targetStr = conditionToTargetStr(condition);
  const resolved  = resolveTarget(targetStr, meta);
  const ops       = opsForType(resolved.fieldType);

  const isMultiValue = ['in', 'notIn', 'includesAny', 'includesAll'].includes(condition.op);
  const hasInlineValue = !isMultiValue && !['filled', 'empty'].includes(condition.op);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Target */}
        <select
          className="input flex-1 min-w-[160px]"
          value={targetStr}
          onChange={(e) => onChange(makeDefaultCondition(e.target.value, meta))}
        >
          <option value="STATUS">{dv.dhaftharu.colStatus}</option>
          {meta.residentFields.length > 0 && (
            <optgroup label={dv.dhaftharu.groupResident}>
              {meta.residentFields.map((f) => (
                <option key={f.id} value={`${RF_PREFIX}${f.id}`}>{f.label}</option>
              ))}
            </optgroup>
          )}
          {meta.registeredBusinessFields.length > 0 && (
            <optgroup label={dv.dhaftharu.groupRegisteredBiz}>
              {meta.registeredBusinessFields.map((f) => (
                <option key={f.id} value={`${RB_PREFIX}${f.id}`}>{f.label}</option>
              ))}
            </optgroup>
          )}
          {meta.homeBusinessFields.length > 0 && (
            <optgroup label={dv.dhaftharu.groupHomeBiz}>
              {meta.homeBusinessFields.map((f) => (
                <option key={f.id} value={`${HB_PREFIX}${f.id}`}>{f.label}</option>
              ))}
            </optgroup>
          )}
        </select>

        {/* Operator */}
        <select
          className="input flex-1 min-w-[120px]"
          value={condition.op}
          onChange={(e) => onChange(withNewOp(condition, e.target.value, resolved))}
        >
          {ops.map((op) => (
            <option key={op} value={op}>{opLabel(op)}</option>
          ))}
        </select>

        {/* Inline value (non-multi ops, skip between which is wider) */}
        {hasInlineValue && condition.op !== 'between' && (
          <div className="flex-1 min-w-[120px]">
            <ConditionValueInput
              condition={condition}
              resolved={resolved}
              meta={meta}
              onChange={onChange}
            />
          </div>
        )}

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          aria-label={dv.common.remove}
          className="btn-ghost shrink-0 px-2 py-1 text-destructive hover:text-destructive"
        >
          <Icon name="delete" size={15} />
        </button>
      </div>

      {/* Between value or multi-select checkbox list — rendered below the selects */}
      {(isMultiValue || condition.op === 'between') && (
        <ConditionValueInput
          condition={condition}
          resolved={resolved}
          meta={meta}
          onChange={onChange}
        />
      )}

      {/* Per-row incomplete warning */}
      {!isConditionComplete(condition) && (
        <p
          className="rounded-lg bg-[hsl(var(--warning-container))] px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--on-warning-container))]"
          role="alert"
        >
          {dv.dhaftharu.incompleteConditions}
        </p>
      )}
    </div>
  );
}

// ── ConditionBuilder (exported) ───────────────────────────────────────────────

export interface ConditionBuilderProps {
  meta: DhaftharuMeta;
  value: Condition[];
  onChange: (conditions: Condition[]) => void;
}

export function ConditionBuilder({ meta, value, onChange }: ConditionBuilderProps): JSX.Element {
  const add = (): void => {
    onChange([...value, makeDefaultCondition('STATUS', meta)]);
  };

  const update = (index: number, c: Condition): void => {
    onChange(value.map((existing, i) => (i === index ? c : existing)));
  };

  const remove = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">{dv.dhaftharu.noConditions}</p>
      ) : (
        <div className="space-y-2">
          {value.map((c, i) => (
            <ConditionRow
              key={i}
              condition={c}
              meta={meta}
              onChange={(updated) => update(i, updated)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}
      <button type="button" onClick={add} className="btn-secondary">
        <Icon name="add" size={15} /> {dv.dhaftharu.addCondition}
      </button>
    </div>
  );
}
